import React, { useState } from 'react';
import {
  Table, Card, Button, Space, Typography, Form, DatePicker,
  Tag, Select, message, Modal, Statistic, Row, Col, Descriptions,
  Drawer, Tooltip
} from 'antd';
import { ReloadOutlined, DownloadOutlined, FileTextOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// ══════════════════════════════════════════════
//  Mock 数据
// ══════════════════════════════════════════════
// TODO: 联调时替换为接口数据
// 列表行按 App + 币种 汇总；countries 明细按 国家 拆解
const MOCK_INCOME = [
  {
    key: '1', billDate: '2026-08-01',
    sourceEntName: '捷顺科技股份有限公司', sourceEntId: 'test-source-001',
    appName: '智能家居控制-iOS-海外', appId: 'test-app-001',
    totalRevenue: '¥32,000.00',
    activeNum: 320, activeDenom: 1024,
    ratio: 80, customCountryCount: 2,
    currency: 'CNY', amount: 8000.00,
    billType: 'system', remark: '',
    countries: [
      { country: '美国', activeNum: 120, activeDenom: 400, ratio: 90, amount: 1200.00, currency: 'CNY' },
      { country: '日本', activeNum: 80, activeDenom: 300, ratio: 80, amount: 640.00, currency: 'CNY' },
      { country: '韩国', activeNum: 120, activeDenom: 324, ratio: 80, amount: 640.00, currency: 'CNY' },
    ],
  },
  {
    key: '2', billDate: '2026-08-01',
    sourceEntName: '捷顺科技股份有限公司', sourceEntId: 'test-source-001',
    appName: '云视监控-Android-国内', appId: 'test-app-002',
    totalRevenue: '¥15,000.00',
    activeNum: 180, activeDenom: 600,
    ratio: 100, customCountryCount: 0,
    currency: 'CNY', amount: 4500.00,
    billType: 'system', remark: '',
    countries: [
      { country: '中国', activeNum: 180, activeDenom: 600, ratio: 100, amount: 4500.00, currency: 'CNY' },
    ],
  },
  {
    key: '3', billDate: '2026-07-01',
    sourceEntName: '东莞康林物联网有限公司', sourceEntId: 'test-source-002',
    appName: '云视监控-iOS-海外', appId: 'test-app-002',
    totalRevenue: '$10,000.00',
    activeNum: 200, activeDenom: 800,
    ratio: 80, customCountryCount: 1,
    currency: 'USD', amount: 2000.00,
    billType: 'system', remark: '',
    countries: [
      { country: '美国', activeNum: 120, activeDenom: 480, ratio: 80, amount: 1200.00, currency: 'USD' },
      { country: '德国', activeNum: 80, activeDenom: 320, ratio: 70, amount: 800.00, currency: 'USD' },
    ],
  },
  {
    key: '4', billDate: '2026-07-01',
    sourceEntName: '捷顺科技股份有限公司', sourceEntId: 'test-source-001',
    appName: '智能家居控制-Android-国内', appId: 'test-app-001',
    totalRevenue: '¥28,000.00',
    activeNum: 128, activeDenom: 1024,
    ratio: 30, customCountryCount: 0,
    currency: 'CNY', amount: 1050.00,
    billType: 'system', remark: '',
    countries: [
      { country: '中国', activeNum: 128, activeDenom: 1024, ratio: 30, amount: 1050.00, currency: 'CNY' },
    ],
  },
  {
    key: '5', billDate: '2026-08-03',
    sourceEntName: '捷顺科技股份有限公司', sourceEntId: 'test-source-001',
    appName: '智能家居控制-iOS-海外', appId: 'test-app-001',
    totalRevenue: '—',
    activeNum: 0, activeDenom: 0,
    ratio: null, customCountryCount: 0,
    currency: 'CNY', amount: -500.00,
    billType: 'adjust', remark: '7月账单多计，冲回差额',
    countries: [],
  },
];

const MOCK_SOURCE_OPTIONS = [
  { value: 'test-source-001', label: '捷顺科技股份有限公司 (test-source-001)' },
  { value: 'test-source-002', label: '东莞康林物联网有限公司 (test-source-002)' },
];

const BILL_TYPE_MAP = { system: '系统出账', adjust: '人工冲账' };

const Income = () => {
  const [dataSource] = useState(MOCK_INCOME);
  const [filterSource, setFilterSource] = useState(undefined);
  const [filterDate, setFilterDate] = useState([]);

  const [exportVisible, setExportVisible] = useState(false);
  const [exportSource, setExportSource] = useState(undefined);
  const [exportMonth, setExportMonth] = useState(null);
  const [exportPreview, setExportPreview] = useState(null);

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  const filteredData = dataSource.filter((r) => {
    if (filterSource && r.sourceEntId !== filterSource) return false;
    if (filterDate && filterDate.length === 2) {
      const bill = new Date(r.billDate).getTime();
      if (bill < filterDate[0].startOf('day').valueOf() || bill > filterDate[1].endOf('day').valueOf()) return false;
    }
    return true;
  });

  const totalCNY = filteredData.filter((r) => r.currency === 'CNY').reduce((sum, r) => sum + (r.amount || 0), 0);
  const totalUSD = filteredData.filter((r) => r.currency === 'USD').reduce((sum, r) => sum + (r.amount || 0), 0);

  const handleExport = () => {
    const ent = MOCK_SOURCE_OPTIONS.find((e) => e.value === exportSource);
    if (!exportSource || !exportMonth) return;
    setExportPreview({
      entName: ent?.label || '',
      month: exportMonth.format('YYYY-MM'),
      totalCNY: 8000.00,
      totalUSD: 0,
    });
  };

  const confirmExport = () => {
    setExportVisible(false);
    setExportPreview(null);
    setExportSource(undefined);
    setExportMonth(null);
    message.success('月账单导出中，请稍候...');
  };

  const columns = [
    { title: '账单日期', dataIndex: 'billDate', width: 110 },
    { title: '分润来源', dataIndex: 'sourceEntName', width: 160, render: (text, r) => <div><Text strong>{text}</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>{r.sourceEntId}</Text></div> },
    { title: '分润 App', dataIndex: 'appName', width: 170, render: (text, r) => <div><Text>{text}</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>{r.appId}</Text></div> },
    { title: '上级总收益', dataIndex: 'totalRevenue', width: 120, render: (v) => v === '—' ? <span style={{ color: '#ccc' }}>—</span> : <Text>{v}</Text> },
    { title: <Space size={4}>活跃设备数<Tooltip title="我的活跃 / 上级总活跃"><Text type="secondary" style={{ fontSize: 12, cursor: 'help' }}>ⓘ</Text></Tooltip></Space>, key: 'active', width: 130,
      render: (_, r) => r.activeDenom > 0
        ? <Text>{r.activeNum}<Text type="secondary" style={{ fontSize: 12 }}>/{r.activeDenom}</Text></Text>
        : <span style={{ color: '#ccc' }}>-</span> },
    { title: '分润比例', key: 'ratio', width: 110,
      render: (_, r) => r.ratio != null
        ? <Space size={4}>{r.ratio}%{r.customCountryCount > 0 && <Tooltip title="该 App 存在自定义国家比例，详情见明细抽屉"><Tag color="orange" style={{ border: 'none', cursor: 'pointer' }}>自定义{r.customCountryCount}国</Tag></Tooltip>}</Space>
        : <span style={{ color: '#ccc' }}>-</span> },
    { title: '币种', dataIndex: 'currency', width: 70, render: (v) => <Tag color={v === 'CNY' ? 'green' : 'blue'} style={{ border: 'none' }}>{v}</Tag> },
    { title: '分润金额', dataIndex: 'amount', width: 130,
      render: (v, r) => <Text strong style={{ color: v < 0 ? '#ff4d4f' : '#52c41a' }}>{r.currency === 'CNY' ? '¥' : '$'}{Math.abs(v).toLocaleString()}</Text> },
    { title: '出账方式', dataIndex: 'billType', width: 105,
      render: (v, r) => v === 'adjust' && r.remark
        ? <Tooltip title={r.remark}><Tag color="orange" style={{ border: 'none', cursor: 'pointer' }}>人工冲账</Tag></Tooltip>
        : <Tag color={v === 'system' ? 'blue' : 'orange'} style={{ border: 'none' }}>{BILL_TYPE_MAP[v]}</Tag> },
    {
      title: '操作', key: 'action', width: 100, fixed: 'right',
      render: (_, r) => <Button type="link" size="small" icon={<FileTextOutlined />} onClick={() => { setDetailRecord(r); setDetailVisible(true); }}>查看明细</Button>,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>分润收益</Title>
        <Button icon={<DownloadOutlined />} onClick={() => setExportVisible(true)}>导出月账单</Button>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <Statistic title="收益总额（¥）" value={totalCNY} precision={2} prefix="¥" valueStyle={{ color: '#52c41a', fontWeight: 'bold' }} />
          </Card>
        </Col>
        <Col span={12}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            <Statistic title="收益总额（$）" value={totalUSD} precision={2} prefix="$" valueStyle={{ color: '#1677ff', fontWeight: 'bold' }} />
          </Card>
        </Col>
      </Row>

      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Form layout="inline">
          <Form.Item label="分润来源" style={{ marginBottom: 0 }}>
            <Select placeholder="选择企业" allowClear showSearch optionFilterProp="children" style={{ width: 240 }} value={filterSource} onChange={setFilterSource}>
              {MOCK_SOURCE_OPTIONS.map((e) => <Option key={e.value} value={e.value}>{e.label}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item label="账单日期" style={{ marginBottom: 0 }}>
            <RangePicker value={filterDate} onChange={setFilterDate} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button icon={<ReloadOutlined />} onClick={() => { setFilterSource(undefined); setFilterDate([]); }}>重置</Button>
          </Form.Item>
        </Form>
      </Card>

      <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="key"
          bordered={false}
          scroll={{ x: 1250 }}
          locale={{ emptyText: '暂无分润收益账单' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
        />
      </Card>

      {/* 明细抽屉（国家拆解） */}
      <Drawer
        title="收益明细（国家拆解）"
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
        width={640}
      >
        {detailRecord && (
          <>
            <Descriptions bordered column={1} size="small" style={{ marginBottom: 24 }}>
              <Descriptions.Item label="账单日期">{detailRecord.billDate}</Descriptions.Item>
              <Descriptions.Item label="分润来源">{detailRecord.sourceEntName}（{detailRecord.sourceEntId}）</Descriptions.Item>
              <Descriptions.Item label="分润 App">{detailRecord.appName}（{detailRecord.appId}）</Descriptions.Item>
              <Descriptions.Item label="上级总收益">{detailRecord.totalRevenue}</Descriptions.Item>
              <Descriptions.Item label="出账方式">
                <Tag color={detailRecord.billType === 'system' ? 'blue' : 'orange'} style={{ border: 'none' }}>{BILL_TYPE_MAP[detailRecord.billType]}</Tag>
              </Descriptions.Item>
              {detailRecord.remark && <Descriptions.Item label="备注">{detailRecord.remark}</Descriptions.Item>}
            </Descriptions>

            {detailRecord.billType === 'system' && detailRecord.countries.length > 0 ? (
              <>
                <div style={{ marginBottom: 8 }}><Text strong>国家收益明细（{detailRecord.currency}）</Text></div>
                <Table
                  size="small"
                  rowKey="country"
                  dataSource={detailRecord.countries}
                  pagination={false}
                  columns={[
                    { title: '国家', dataIndex: 'country', width: 100 },
                    { title: '活跃设备数', key: 'active', width: 140,
                      render: (_, c) => <Text>{c.activeNum}<Text type="secondary" style={{ fontSize: 12 }}>/{c.activeDenom}</Text></Text> },
                    { title: '分润比例', dataIndex: 'ratio', width: 90, render: (v) => <Text style={{ color: '#52c41a' }}>{v}%</Text> },
                    { title: '分润金额', dataIndex: 'amount', width: 130, align: 'right',
                      render: (v, c) => <Text strong>{c.currency === 'CNY' ? '¥' : '$'}{Math.abs(v).toLocaleString()}</Text> },
                  ]}
                />
                <div style={{ marginTop: 16, textAlign: 'right' }}>
                  <Text type="secondary">合计：</Text>
                  <Text strong style={{ color: '#52c41a', fontSize: 16 }}>
                    {detailRecord.currency === 'CNY' ? '¥' : '$'}{Math.abs(detailRecord.amount).toLocaleString()}
                  </Text>
                </div>
              </>
            ) : (
              <Text type="secondary">该记录为人工冲账，无国家明细。</Text>
            )}
          </>
        )}
      </Drawer>

      {/* 导出月账单 Modal */}
      <Modal title="导出月账单" open={exportVisible} onCancel={() => { setExportVisible(false); setExportPreview(null); }} footer={null} width={500}>
        {!exportPreview ? (
          <>
            <Form layout="vertical" style={{ marginTop: 16 }}>
              <Form.Item label="分润来源">
                <Select placeholder="选择企业" allowClear showSearch optionFilterProp="children" value={exportSource} onChange={setExportSource}>
                  {MOCK_SOURCE_OPTIONS.map((e) => <Option key={e.value} value={e.value}>{e.label}</Option>)}
                </Select>
              </Form.Item>
              <Form.Item label="账单月份">
                <DatePicker picker="month" value={exportMonth} onChange={setExportMonth} style={{ width: '100%' }} placeholder="选择月份" />
              </Form.Item>
            </Form>
            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <Button style={{ marginRight: 8 }} onClick={() => setExportVisible(false)}>取消</Button>
              <Button type="primary" disabled={!exportSource || !exportMonth} onClick={handleExport}>查询预览</Button>
            </div>
          </>
        ) : (
          <>
            <Descriptions bordered column={1} size="small" style={{ marginTop: 16, marginBottom: 24 }}>
              <Descriptions.Item label="企业">{exportPreview.entName}</Descriptions.Item>
              <Descriptions.Item label="月份">{exportPreview.month}</Descriptions.Item>
              <Descriptions.Item label="收益总额（¥）">¥{exportPreview.totalCNY.toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="收益总额（$）">${exportPreview.totalUSD.toLocaleString()}</Descriptions.Item>
            </Descriptions>
            <div style={{ textAlign: 'right' }}>
              <Button style={{ marginRight: 8 }} onClick={() => setExportPreview(null)}>返回</Button>
              <Button type="primary" icon={<DownloadOutlined />} onClick={confirmExport}>导出 Excel</Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Income;
